/**
 * All shop prices are TTC (tax included) in MAD. Never compute VAT on top —
 * `vatRate` from the API is informational only, for the "TTC" badge.
 */

const FALLBACK_LOCALE = 'fr-FR';
const PRIMARY_LOCALE = 'fr-MA';

/**
 * ICU renders MAD inconsistently across engines ("257,00 MAD" vs "257,00 DH"),
 * so we format the number and append the currency ourselves. Consistent output
 * matters more here than locale-perfect symbol placement.
 */
export const formatPrice = (
  amount: number | null | undefined,
  currency: string = 'MAD'
): string => {
  const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;

  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(PRIMARY_LOCALE, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    try {
      formatted = new Intl.NumberFormat(FALLBACK_LOCALE, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      formatted = value.toFixed(2);
    }
  }

  return `${formatted} ${currency}`;
};

/** Whole-number variant for thresholds, e.g. "Livraison gratuite dès 500 MAD". */
export const formatPriceCompact = (
  amount: number | null | undefined,
  currency: string = 'MAD'
): string => {
  const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  if (Number.isInteger(value)) {
    try {
      return `${new Intl.NumberFormat(PRIMARY_LOCALE).format(value)} ${currency}`;
    } catch {
      return `${value} ${currency}`;
    }
  }
  return formatPrice(value, currency);
};
